// components/admin/AdminAccessCodes.jsx
import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../firebase';

const STUCK_AGE_MIN = 10;

const PROVIDER_LABELS = { nuki: 'Nuki', seam: 'Yale (Seam)' };

const NUKI_TEST_STEP_LABELS = {
  deviceStatus: 'Kilit çevrimiçi',
  keypad: 'Keypad eşleşmiş',
  timezone: 'Saat dilimi İstanbul',
  createCode: 'Test kodu oluşturuldu',
  getCode: 'Kod okundu',
  windowRoundTrip: 'Zaman aralığı doğru kaydedildi',
  deleteCode: 'Test kodu silindi',
};

const AdminAccessCodes = () => {
  const [accessCodes, setAccessCodes] = useState([]);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, expired, stuck
  const [lockStatus, setLockStatus] = useState(null);
  const [retrying, setRetrying] = useState({});
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [doorTest, setDoorTest] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);

  const providerLabel = PROVIDER_LABELS[provider || lockStatus?.provider] || 'Kilit';

  useEffect(() => {
    fetchAccessCodes();
    // Refresh the lock status once, then follow it through Firestore
    const refreshDeviceCallable = httpsCallable(functions, 'getLockStatus');
    refreshDeviceCallable().catch((e) => console.warn('getLockStatus failed:', e?.message));

    const unsub = onSnapshot(doc(db, 'system', 'lockStatus'), (snap) => {
      if (snap.exists()) setLockStatus(snap.data());
    });
    return () => unsub();
  }, []);

  const fetchAccessCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const listAccessCodesCallable = httpsCallable(functions, 'listAccessCodes');
      const result = await listAccessCodesCallable();
      setAccessCodes(result.data.accessCodes || []);
      setProvider(result.data.provider || null);
    } catch (err) {
      console.error('Access codes yüklenirken hata:', err);
      setError(err.message || 'Access codes yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupOrphans = async () => {
    if (!window.confirm(`${providerLabel} kilidindeki, aktif bir rezervasyona ait olmayan sistem kodları ve süresi dolmuş kodlar silinecek. Elle eklenmiş geçerli kodlara dokunulmaz. Devam edilsin mi?`)) return;
    try {
      setCleanupRunning(true);
      setActionMessage(null);
      const cleanup = httpsCallable(functions, 'cleanupOrphanedCodes');
      const result = await cleanup();
      const d = result.data || {};
      setActionMessage(
        `Temizlik bitti: ${d.deleted} kod silindi (toplam: ${d.totalCodes}, aktif rezervasyon: ${d.skippedActive}, elle eklenmiş: ${d.skippedManual ?? 0}).` +
        (d.stoppedForRateLimit ? ' Hız sınırına takıldı, kalanlar için tekrar çalıştırın.' : '')
      );
      await fetchAccessCodes();
    } catch (err) {
      setActionMessage(`Hata: ${err.message || 'Temizlik başarısız'}`);
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleDoorTest = async () => {
    if (!window.confirm('Şu andan itibaren 15 dakika geçerli bir kapı kodu oluşturulacak ve size gösterilecek. Keypad\'de deneyip kapının açıldığını doğrulayın. Devam edilsin mi?')) return;
    try {
      setTestRunning(true);
      setDoorTest(null);
      setTestResult(null);
      setActionMessage(null);
      const test = httpsCallable(functions, 'testNukiConnection');
      const result = await test({ doorTest: true });
      if (result.data?.pin) {
        setDoorTest(result.data);
      } else {
        setTestResult(result.data);
      }
    } catch (err) {
      setTestResult({ success: false, steps: [{ step: 'createCode', ok: false, detail: err.message || 'Kapı testi başarısız' }] });
    } finally {
      setTestRunning(false);
    }
  };

  const handleNukiTest = async () => {
    if (!window.confirm('Nuki kilidinde 2 gün sonrası için 10 dakikalık bir test kodu oluşturulup hemen silinecek. Devam edilsin mi?')) return;
    try {
      setTestRunning(true);
      setTestResult(null);
      setActionMessage(null);
      const test = httpsCallable(functions, 'testNukiConnection');
      const result = await test();
      setTestResult(result.data);
    } catch (err) {
      setTestResult({ success: false, steps: [{ step: 'call', ok: false, detail: err.message || 'Test başarısız' }] });
    } finally {
      setTestRunning(false);
    }
  };

  const handleRetry = async (accessCodeId) => {
    if (!window.confirm('Bu sıkışmış kod için manuel retry tetiklenecek. Devam edilsin mi?')) return;
    try {
      setRetrying((s) => ({ ...s, [accessCodeId]: true }));
      setActionMessage(null);
      const retryCallable = httpsCallable(functions, 'retryAccessCode');
      const result = await retryCallable({ accessCodeId });
      const data = result.data || {};

      if (data.success) {
        const status = data.status || 'unknown';
        const recreatedText = data.recreated ? 'yeniden oluşturuldu' : 'yeniden oluşturulmadı';
        setActionMessage(
          `Retry tamamlandı (${data.reservationId || '-'}): durum=${status}, ${recreatedText}.`
        );
      } else {
        setActionMessage('Retry çağrısı başarısız oldu.');
      }

      await fetchAccessCodes();
    } catch (err) {
      console.error('retryAccessCode hatası:', err);
      setActionMessage(`Hata: ${err.message || 'Retry başarısız'}`);
    } finally {
      setRetrying((s) => ({ ...s, [accessCodeId]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      set: 'bg-green-600 text-white',
      setting: 'bg-yellow-600 text-white',
      removing: 'bg-orange-600 text-white',
      unset: 'bg-gray-600 text-white',
      disabled: 'bg-gray-600 text-white',
      failed: 'bg-red-600 text-white',
    };

    const statusLabels = {
      set: 'Aktif',
      setting: 'Ayarlanıyor',
      removing: 'Kaldırılıyor',
      unset: 'Pasif',
      disabled: 'Devre dışı',
      failed: 'Hata',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-600 text-white'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const isExpired = (endsAt) => {
    if (!endsAt) return false;
    return new Date(endsAt) < new Date();
  };

  const isActive = (startsAt, endsAt) => {
    if (!startsAt || !endsAt) return false;
    const now = new Date();
    return new Date(startsAt) <= now && new Date(endsAt) >= now;
  };

  const isStuck = (ac) => {
    if (ac.status === 'set' || ac.status === 'disabled') return false;
    if (!ac.created_at) return false;
    const ageMin = (Date.now() - new Date(ac.created_at).getTime()) / 60000;
    return ageMin > STUCK_AGE_MIN;
  };

  const getFilteredCodes = () => {
    if (filter === 'all') return accessCodes;
    if (filter === 'active') return accessCodes.filter(ac => isActive(ac.starts_at, ac.ends_at) && !isExpired(ac.ends_at));
    if (filter === 'expired') return accessCodes.filter(ac => isExpired(ac.ends_at));
    if (filter === 'stuck') return accessCodes.filter(isStuck);
    return accessCodes;
  };

  const stuckCount = accessCodes.filter(isStuck).length;

  const formatRelative = (ts) => {
    if (!ts) return '-';
    const ms = Date.now() - new Date(ts.toDate ? ts.toDate() : ts).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'az önce';
    if (min < 60) return `${min} dk önce`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} sa önce`;
    return `${Math.floor(hr / 24)} gün önce`;
  };

  const filteredCodes = getFilteredCodes();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-600 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-red-300 font-semibold">Hata</h3>
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchAccessCodes}
          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          Tekrar Dene
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Kapı Kodları ({providerLabel})</h2>
          <p className="text-gray-400 text-sm mt-1">
            {providerLabel} kilidindeki tüm keypad kodları
          </p>
        </div>
        <div className="flex gap-2">
          {(provider || lockStatus?.provider) === 'nuki' && (
            <>
              <button
                onClick={handleNukiTest}
                disabled={testRunning}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                title="Nuki bağlantısını uçtan uca test et (geçici kod açar ve siler)"
              >
                {testRunning ? 'Test ediliyor...' : 'Nuki Testi'}
              </button>
              <button
                onClick={handleDoorTest}
                disabled={testRunning}
                className="px-4 py-2 bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white rounded-lg transition-colors"
                title="15 dakika geçerli bir kod üret ve keypad'de dene"
              >
                Kapı Testi
              </button>
            </>
          )}
          <button
            onClick={handleCleanupOrphans}
            disabled={cleanupRunning}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center"
            title="Aktif rezervasyona ait olmayan sistem kodlarını ve süresi dolmuş kodları sil"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
            </svg>
            {cleanupRunning ? 'Temizleniyor...' : 'Sahipsiz Temizle'}
          </button>
          <button
            onClick={fetchAccessCodes}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Yenile
          </button>
        </div>
      </div>

      {/* Cihaz Durum Banner'ı */}
      {lockStatus && (
        <div className={`rounded-lg p-4 border flex items-center justify-between ${
          lockStatus.online === true
            ? 'bg-green-900/20 border-green-600'
            : lockStatus.online === false
              ? 'bg-red-900/20 border-red-600'
              : 'bg-gray-800 border-gray-700'
        }`}>
          <div className="flex items-center">
            <span className={`w-3 h-3 rounded-full mr-3 ${
              lockStatus.online === true ? 'bg-green-400 animate-pulse' :
              lockStatus.online === false ? 'bg-red-400' : 'bg-gray-500'
            }`}></span>
            <div>
              <div className="text-white font-semibold">
                {lockStatus.name || providerLabel} ({PROVIDER_LABELS[lockStatus.provider] || 'Kilit'}) —{' '}
                {lockStatus.online === true ? 'Online' : lockStatus.online === false ? 'OFFLINE (yeni kodlar kilit bağlanınca yazılır)' : 'Bilinmiyor'}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">
                {typeof lockStatus.batteryLevel === 'number' && <>Pil: %{lockStatus.batteryLevel} · </>}
                {lockStatus.keypadPaired === false && <span className="text-red-300">Keypad eşleşmemiş · </span>}
                {typeof lockStatus.activeCodeCount === 'number' && lockStatus.maxCodeCount && (
                  <>Kod: {lockStatus.activeCodeCount}/{lockStatus.maxCodeCount} · </>
                )}
                Son kontrol: {formatRelative(lockStatus.lastCheckedAt)}
                {lockStatus.lastWebhookAt && <> · Son webhook: {formatRelative(lockStatus.lastWebhookAt)}</>}
              </div>
              {(lockStatus.batteryCritical || lockStatus.keypadBatteryCritical) && (
                <div className="text-xs text-amber-300 mt-1">
                  {lockStatus.batteryCritical && 'Kilit pili kritik seviyede. '}
                  {lockStatus.keypadBatteryCritical && 'Keypad pili kritik seviyede.'}
                </div>
              )}
              {lockStatus.error && (
                <div className="text-xs text-red-300 mt-1">Kilit API hatası: {lockStatus.error}</div>
              )}
            </div>
          </div>
          {stuckCount > 0 && (
            <span className="px-3 py-1 bg-amber-600 text-white text-sm rounded-full font-semibold">
              {stuckCount} sıkışmış kod
            </span>
          )}
        </div>
      )}

      {/* Door test code */}
      {doorTest && (
        <div className="rounded-lg p-4 border bg-teal-900/20 border-teal-600">
          <div className="text-white font-semibold mb-1">Kapı test kodu</div>
          <div className="text-3xl font-mono font-bold tracking-widest text-teal-200 my-2">{doorTest.pin}</div>
          <div className="text-sm text-gray-300">
            {formatDate(doorTest.endsAt)} saatine kadar geçerli. Keypad'de girip kapının açıldığını doğrulayın;
            süre dolunca kod kendiliğinden geçersiz olur ve saatlik temizlikte silinir.
          </div>
          {doorTest.status !== 'set' && (
            <div className="text-xs text-amber-300 mt-1">
              Kod henüz kilide yazılmadı (durum: {doorTest.status}); birkaç saniye bekleyip deneyin.
            </div>
          )}
        </div>
      )}

      {/* Nuki test result */}
      {testResult && (
        <div className={`rounded-lg p-4 border ${testResult.success ? 'bg-green-900/20 border-green-600' : 'bg-red-900/20 border-red-600'}`}>
          <div className="text-white font-semibold mb-2">
            {testResult.success ? 'Nuki testi başarılı' : 'Nuki testi başarısız'}
          </div>
          <ul className="space-y-1 text-sm">
            {(testResult.steps || []).map((s) => (
              <li key={s.step} className={s.ok ? 'text-green-300' : 'text-red-300'}>
                {s.ok ? '✓' : '✗'} {NUKI_TEST_STEP_LABELS[s.step] || s.step}
                {!s.ok && s.detail && (
                  <span className="text-gray-400"> — {typeof s.detail === 'string' ? s.detail : JSON.stringify(s.detail)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Mesajı */}
      {actionMessage && (
        <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-3 text-blue-200 text-sm">
          {actionMessage}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex space-x-2 border-b border-gray-700">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'all'
              ? 'text-indigo-400 border-b-2 border-indigo-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Tümü ({accessCodes.length})
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'active'
              ? 'text-green-400 border-b-2 border-green-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Aktif ({accessCodes.filter(ac => isActive(ac.starts_at, ac.ends_at) && !isExpired(ac.ends_at)).length})
        </button>
        <button
          onClick={() => setFilter('stuck')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'stuck'
              ? 'text-amber-400 border-b-2 border-amber-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Sıkışmış ({stuckCount})
        </button>
        <button
          onClick={() => setFilter('expired')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'expired'
              ? 'text-red-400 border-b-2 border-red-400'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Süresi Dolmuş ({accessCodes.filter(ac => isExpired(ac.ends_at)).length})
        </button>
      </div>

      {/* Access Codes Table */}
      {filteredCodes.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-12 text-center">
          <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p className="text-gray-400 text-lg">
            {filter === 'all' && 'Henüz erişim kodu oluşturulmamış'}
            {filter === 'active' && 'Aktif erişim kodu bulunmuyor'}
            {filter === 'expired' && 'Süresi dolmuş erişim kodu bulunmuyor'}
          </p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    İsim
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Kod
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Başlangıç
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Bitiş
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredCodes.map((ac) => {
                  const expired = isExpired(ac.ends_at);
                  const active = isActive(ac.starts_at, ac.ends_at);
                  
                  return (
                    <tr 
                      key={ac.access_code_id} 
                      className={`hover:bg-gray-700/50 transition-colors ${
                        expired ? 'opacity-60' : ''
                      } ${
                        active ? 'bg-green-900/10' : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-200 font-medium">
                          {ac.name || '-'}
                          {ac.managed === false && (
                            <span className="ml-2 px-2 py-0.5 rounded bg-gray-700 text-gray-300 text-[10px] uppercase" title="Elle eklenmiş kod; süresi dolmadıkça sistem silmez">
                              manuel
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{ac.access_code_id}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-lg font-mono font-bold text-indigo-400">
                          {ac.code || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {getStatusBadge(ac.status)}
                          {active && (
                            <span className="text-xs text-green-400 flex items-center">
                              <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
                              Şu an aktif
                            </span>
                          )}
                          {expired && (
                            <span className="text-xs text-red-400">Süresi doldu</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {formatDate(ac.starts_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">
                        {formatDate(ac.ends_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {formatDate(ac.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        {isStuck(ac) ? (
                          <button
                            onClick={() => handleRetry(ac.access_code_id)}
                            disabled={!!retrying[ac.access_code_id]}
                            className="px-3 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-xs rounded transition-colors"
                            title="Sıkışmış kod için manuel retry çalıştır"
                          >
                            {retrying[ac.access_code_id] ? '...' : 'Tekrar Dene'}
                          </button>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="text-gray-400 text-sm">Toplam Kod</div>
          <div className="text-white text-2xl font-bold mt-1">{accessCodes.length}</div>
        </div>
        <div className="bg-green-900/20 border border-green-600 rounded-lg p-4">
          <div className="text-green-400 text-sm">Aktif Kodlar</div>
          <div className="text-white text-2xl font-bold mt-1">
            {accessCodes.filter(ac => isActive(ac.starts_at, ac.ends_at) && !isExpired(ac.ends_at)).length}
          </div>
        </div>
        <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
          <div className="text-red-400 text-sm">Süresi Dolmuş</div>
          <div className="text-white text-2xl font-bold mt-1">
            {accessCodes.filter(ac => isExpired(ac.ends_at)).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccessCodes;
